import moment from 'moment'
import type { Moment } from 'moment'
import { TripEventType } from 'app/constants/delegation/tripEventType'
import { TmsRouteType } from 'app/constants/tmsRouteType'
import { EventModel } from 'app/models/newDelegation/eventModel'
import { TmsDataModel } from 'app/models/tmsDataModel'
import { TIME_FORMAT } from 'app/constants/date'

// Duplikat zdarzenia TMS to ten sam dokument, nie ta sama godzina.
export const tmsEventKey = (
  tripEvent: TripEventType,
  docNumber: string | null | undefined,
  date: Moment | Date | string,
): string => `${tripEvent}|${docNumber ?? ''}|${moment(date).format(TIME_FORMAT)}`

const POLAND_COUNTRY_CODE = 40
const POLAND_COUNTRY = { code: 'pl', name: 'Polska', nationId: 40, id: '1' }

export const buildSyntheticEventsFromTmsData = (
  tmsData: Array<TmsDataModel>,
  dateFrom: Moment,
  dateTo: Moment,
): Array<EventModel> => {
  if (!tmsData || tmsData.length === 0) return []

  const events: EventModel[] = []
  let lp = 0

  // Begin event to bracket the delegation set
  events.push({
    tripEvent: TripEventType.Begin,
    date: dateFrom,
    lp: lp++,
    country: POLAND_COUNTRY,
  })

  // Loading/Unloading pairs (same pattern as TmsImportView.tsx)
  for (const tms of tmsData) {
    events.push({
      tripEvent: TripEventType.Loading,
      date: tms.loadingDate,
      docNumber: tms.docNumber,
      tmsRouteType: tms.routeType,
      country: tms.loadingCountry,
      secondDate: tms.unloadingDate,
      secondCountry: tms.unloadingCountry,
      lp: lp++,
    })
    events.push({
      tripEvent: TripEventType.Unloading,
      date: tms.unloadingDate,
      docNumber: tms.docNumber,
      tmsRouteType: tms.routeType,
      country: tms.unloadingCountry,
      secondDate: tms.loadingDate,
      secondCountry: tms.loadingCountry,
      lp: lp++,
    })
  }

  // Finish event
  events.push({
    tripEvent: TripEventType.Finish,
    date: dateTo,
    lp: lp++,
    country: POLAND_COUNTRY,
  })

  return events
}

const sortEvents = (a: EventModel, b: EventModel) => {
  if (a.date < b.date) return -1
  if (a.date > b.date) return 1
  return 0
}

const sortArray = (a: any, b: any, prop: string) => {
  if (a[prop] < b[prop]) return -1
  if (a[prop] > b[prop]) return 1
  return 0
}

const groupBy = (items, key) =>
  items.reduce(
    (result, item) => ({
      ...result,
      [item[key]]: [...(result[item[key]] || []), item],
    }),
    {},
  )

export const constructTmsData = (
  allEvents: Array<EventModel>,
  startDate: Moment,
): Array<TmsDataModel> => {
  if (allEvents == null || allEvents.length == 0) return []

  const orderedTripItems = allEvents.sort((a, b) => sortArray(a, b, 'date'))
  const delegationSets: Array<Array<EventModel>> = []

  let currentSet: Array<EventModel> = []
  delegationSets.push(currentSet)

  const previousItem = allEvents[0]
  currentSet.push(previousItem)

  for (let i = 1; i < orderedTripItems.length; i++) {
    const currentItem = orderedTripItems[i]
    if (currentItem.tripEvent == TripEventType.Begin) {
      currentSet = []
      delegationSets.push(currentSet)
      currentSet.push(currentItem)
    } else {
      currentSet.push(currentItem)
    }
  }

  for (const delegationSet of delegationSets) {
    if (
      !delegationSet.some(
        (t) =>
          t.tripEvent == TripEventType.Loading ||
          t.tripEvent == TripEventType.Unloading,
      )
    ) {
      continue
    }

    for (let i = delegationSet.length - 1; i >= 0; i--) {
      const tripItem = delegationSet[i]
      if (
        tripItem.tripEvent != TripEventType.Loading &&
        tripItem.tripEvent != TripEventType.Unloading
      ) {
        continue
      }

      let correspondingItem = delegationSet.find(
        (d) =>
          d.docNumber == tripItem.docNumber &&
          d.tripEvent != tripItem.tripEvent,
      )
      if (correspondingItem == null) {
        correspondingItem = orderedTripItems.find(
          (d) =>
            d.docNumber == tripItem.docNumber &&
            d.tripEvent != tripItem.tripEvent,
        )
        if (correspondingItem != null) {
          delegationSet.push({
            docNumber: correspondingItem.docNumber,
            date: correspondingItem.date,
            lp: correspondingItem.lp,
            tripEvent: correspondingItem.tripEvent,
            country: correspondingItem.country,
            registrationNumber: correspondingItem.registrationNumber,
            tmsRouteType: correspondingItem.tmsRouteType,
            secondDate: correspondingItem.secondDate,
            secondCountry: correspondingItem.secondCountry,
            vehicleType: correspondingItem.vehicleType,
            vehicleId: correspondingItem.vehicleId,
          })
        }
      }
    }
  }

  const allTmsDataItems: Array<TmsDataModel> = []
  for (const delegationSet of delegationSets) {
    if (
      !delegationSet.some(
        (t) =>
          t.tripEvent == TripEventType.Loading ||
          t.tripEvent == TripEventType.Unloading,
      )
    )
      continue

    if (!delegationSet.some((t) => t.tripEvent == TripEventType.Begin)) continue

    const events = delegationSet
    let tmsDataItems: Array<TmsDataModel> = []

    const groups = groupBy(
      events.filter(
        (e) =>
          e.tripEvent == TripEventType.Loading ||
          e.tripEvent == TripEventType.Unloading,
      ),
      'docNumber',
    )
    const bilateralGroups = groupBy(
      events.filter(
        (e) =>
          (e.tripEvent == TripEventType.Loading ||
            e.tripEvent == TripEventType.Unloading) &&
          (e.tmsRouteType == TmsRouteType.Bilateral ||
            e.tmsRouteType == TmsRouteType.Import ||
            e.tmsRouteType == TmsRouteType.Export),
      ),
      'docNumber',
    )

    const groupsArray = Object.keys(groups)
      .map((key) => [key, groups[key]])
      .filter((g) => g[1].length == 2)
    const bilateralGroupsArray = Object.keys(bilateralGroups)
      .map((key) => [key, bilateralGroups[key]])
      .filter((g) => g[1].length == 2)

    if (groupsArray != null) {
      for (const group of groupsArray) {
        const markers: Array<EventModel> = group[1].sort((a, b) =>
          sortArray(a, b, 'date'),
        )
        let previous: EventModel = markers[0]

        const beginItem = delegationSet.find(
          (i) => i.tripEvent == TripEventType.Begin,
        )
        const finishItem = delegationSet.find(
          (i) => i.tripEvent == TripEventType.Finish,
        )

        for (let i = 1; i < markers.length; i++) {
          if (previous.tripEvent == TripEventType.Unloading) {
            previous = markers[i]
            continue
          }

          const current = markers[i]
          if (current.tripEvent == TripEventType.Unloading) {
            const groupStart = previous.date
            const groupEnd = current.date

            if (
              bilateralGroupsArray.length == 0 ||
              current.tmsRouteType != TmsRouteType.CrossTrade
            ) {
              tmsDataItems.push({
                docNumber: current.docNumber,
                loadingDate: groupStart,
                unloadingDate: groupEnd,
                displayedLoadingDate:
                  beginItem == null || groupStart > beginItem.date
                    ? groupStart
                    : beginItem.date,
                displayedUnloadingDate:
                  finishItem == null || groupEnd < finishItem.date
                    ? groupEnd
                    : finishItem.date,
                routeType:
                  current.tmsRouteType == TmsRouteType.CrossTrade
                    ? TmsRouteType.CrossTradeWrong
                    : current.tmsRouteType,
                loadingCountry: previous.country,
                unloadingCountry: current.country,
              })
            } else {
              determineGoodAndWrongCrossTradePeriods(
                bilateralGroupsArray,
                tmsDataItems,
                previous,
                current,
                groupStart,
                groupEnd,
                beginItem == null ? groupStart : beginItem.date,
                finishItem == null ? groupEnd : finishItem.date,
              )
            }
          }

          if (markers[i].tripEvent == TripEventType.Loading) {
            previous = markers[i]
          }
        }
      }
    }

    tmsDataItems = tmsDataItems.sort((a, b) =>
      sortArray(a, b, 'displayedLoadingDate'),
    )

    determineWrongCrossTradePeriodsWithinBilateral(tmsDataItems, events)
    determineTransitPeriods(tmsDataItems, events)
    determineEmptyCabotagePeriods(tmsDataItems, events)
    determineEmptyCrossTradePeriods(tmsDataItems, events)
    determinePolishPeriods(tmsDataItems, events)
    removeCabotageInsideTransitPeriods(tmsDataItems, events)
    removeCrossTradeInsideTransitPeriods(tmsDataItems, events)
    determineEmptyPeriods(tmsDataItems, events)

    tmsDataItems = tmsDataItems
      .filter(
        (a) =>
          a.unloadingDate > startDate &&
          a.loadingDate < startDate.clone().add(1, 'days'),
      )
      .sort((a, b) => sortArray(a, b, 'displayedLoadingDate'))
    allTmsDataItems.push(...tmsDataItems)
  }

  return allTmsDataItems
}

const determineGoodAndWrongCrossTradePeriods = (
  bilateralGroups: Array<any>,
  tmsData: Array<TmsDataModel>,
  previous: EventModel,
  current: EventModel,
  groupStart: Moment,
  groupEnd: Moment,
  beginDate: Moment,
  finishDate: Moment,
) => {
  let goodRanges = []
  let wrongRanges = [
    { dateFrom: groupStart, dateTo: groupEnd, isVisited: false },
  ]

  for (const bilateralGroup of bilateralGroups) {
    const bilateralLoadingInfo: EventModel = bilateralGroup[1][0]
    const bilateralUnloadingInfo: EventModel = bilateralGroup[1][1]

    const bilateralStart = bilateralLoadingInfo.date
    const bilateralEnd = bilateralUnloadingInfo.date

    const associatedTms: Array<TmsDataModel> = [
      {
        docNumber: bilateralLoadingInfo.docNumber,
        displayedLoadingDate:
          bilateralStart > beginDate ? bilateralStart : beginDate,
        displayedUnloadingDate:
          bilateralEnd < finishDate ? bilateralEnd : finishDate,
        loadingDate: bilateralStart,
        unloadingDate: bilateralEnd,
        routeType: bilateralUnloadingInfo.tmsRouteType,
        loadingCountry: bilateralLoadingInfo.country,
        unloadingCountry: bilateralUnloadingInfo.country,
      },
    ]

    const innerWrongRanges = []
    for (const wrongRange of wrongRanges.filter((r) => !r.isVisited)) {
      const wrongRangeStart = wrongRange.dateFrom
      const wrongRangeEnd = wrongRange.dateTo
      wrongRange.isVisited = true

      let visited = false
      if (bilateralEnd > wrongRangeStart && bilateralStart < wrongRangeEnd) {
        visited = true
        if (
          wrongRangeStart >= bilateralStart &&
          wrongRangeEnd <= bilateralEnd
        ) {
          goodRanges.push({
            dateFrom: wrongRangeStart,
            dateTo: wrongRangeEnd,
            associatedTms: associatedTms,
          })
        } else if (
          wrongRangeStart < bilateralStart &&
          wrongRangeEnd < bilateralEnd
        ) {
          goodRanges.push({
            dateFrom: bilateralStart,
            dateTo: wrongRangeEnd,
            associatedTms: associatedTms,
          })
          innerWrongRanges.push({
            dateFrom: wrongRangeStart,
            dateTo: bilateralStart,
            isVisited: false,
          })
        } else if (
          wrongRangeStart > bilateralStart &&
          wrongRangeEnd > bilateralEnd
        ) {
          goodRanges.push({
            dateFrom: wrongRangeStart,
            dateTo: bilateralEnd,
            associatedTms: associatedTms,
          })
          innerWrongRanges.push({
            dateFrom: bilateralEnd,
            dateTo: wrongRangeEnd,
            isVisited: false,
          })
        } else if (
          wrongRangeStart < bilateralStart &&
          wrongRangeEnd > bilateralEnd
        ) {
          goodRanges.push({
            dateFrom: bilateralStart,
            dateTo: bilateralEnd,
            associatedTms: associatedTms,
          })
          innerWrongRanges.push({
            dateFrom: wrongRangeStart,
            dateTo: bilateralStart,
            isVisited: false,
          })
          innerWrongRanges.push({
            dateFrom: bilateralEnd,
            dateTo: wrongRangeEnd,
            isVisited: false,
          })
        }
      }

      wrongRange.isVisited = visited
    }

    wrongRanges.push(...innerWrongRanges)
  }

  goodRanges = goodRanges.sort((a, b) => sortArray(a, b, 'dateFrom'))
  wrongRanges = wrongRanges
    .filter((r) => !r.isVisited)
    .sort((a, b) => sortArray(a, b, 'dateFrom'))

  for (let j = 0; j < goodRanges.length; j++) {
    const goodRange = goodRanges[j]

    tmsData.push({
      docNumber: current.docNumber,
      loadingDate: groupStart,
      unloadingDate: groupEnd,
      displayedLoadingDate: goodRange.dateFrom,
      displayedUnloadingDate: goodRange.dateTo,
      routeType: TmsRouteType.CrossTradeBilateral,
      loadingCountry: previous.country,
      unloadingCountry: current.country,
      associatedTms: goodRange.associatedTms,
    })
  }

  for (let j = 0; j < wrongRanges.length; j++) {
    const wrongRange = wrongRanges[j]

    tmsData.push({
      docNumber: current.docNumber,
      loadingDate: groupStart,
      unloadingDate: groupEnd,
      displayedLoadingDate: wrongRange.dateFrom,
      displayedUnloadingDate: wrongRange.dateTo,
      routeType: TmsRouteType.CrossTradeWrong,
      loadingCountry: previous.country,
      unloadingCountry: current.country,
    })
  }
}

const determineWrongCrossTradePeriodsWithinBilateral = (
  tmsData: Array<TmsDataModel>,
  tripItems: Array<EventModel>,
) => {
  let bilateralIdentifier: string = null

  let secondCrossTradeGood = false
  const crossTradeItems = tmsData.filter(
    (t) => t.routeType == TmsRouteType.CrossTradeBilateral,
  )
  for (const currentTms of crossTradeItems) {
    if (
      currentTms.associatedTms != null &&
      currentTms.associatedTms.length > 0
    ) {
      const currentGroupIdentifier = currentTms.associatedTms[0].docNumber
      if (currentGroupIdentifier == bilateralIdentifier) {
        const precedingBilaterals = tmsData
          .filter(
            (t) =>
              t.routeType == TmsRouteType.Export &&
              t.unloadingDate < currentTms.loadingDate,
          )
          .sort((a, b) => sortArray(a, b, 'unloading'))
        if (precedingBilaterals.length > 0) {
          const precedingBilateral =
            precedingBilaterals[precedingBilaterals.length - 1]
          const otherCrossTradeInPreviousBilateral = tmsData.some(
            (t) =>
              t.routeType == TmsRouteType.CrossTradeBilateral &&
              t.associatedTms != null &&
              t.associatedTms.filter(
                (a) => a.docNumber == precedingBilateral.docNumber,
              ).length > 0,
          )
          if (!otherCrossTradeInPreviousBilateral) {
            if (secondCrossTradeGood) {
              currentTms.routeType = TmsRouteType.CrossTradeWrong
              currentTms.associatedTms = null
            }
            secondCrossTradeGood = true
          } else {
            currentTms.routeType = TmsRouteType.CrossTradeWrong
            currentTms.associatedTms = null
          }
        } else {
          currentTms.routeType = TmsRouteType.CrossTradeWrong
          currentTms.associatedTms = null
        }
      }

      if (currentGroupIdentifier != bilateralIdentifier) {
        secondCrossTradeGood = false
      }
      bilateralIdentifier = currentGroupIdentifier
    }
  }
}

const determineTransitPeriods = (
  tmsData: Array<TmsDataModel>,
  tripItems: Array<EventModel>,
) => {
  const countryChangeMarkers = tripItems
    .filter(
      (t) =>
        t.tripEvent == TripEventType.ChangeCountry ||
        t.tripEvent == TripEventType.Begin ||
        t.tripEvent == TripEventType.Finish,
    )
    .sort(sortEvents)

  let previousCountryChange: EventModel = null
  if (countryChangeMarkers.length > 0) {
    previousCountryChange = countryChangeMarkers[0]
  }

  const potentialTransitPeriods = []
  for (let i = 1; i < countryChangeMarkers.length; i++) {
    if (
      previousCountryChange.tripEvent == TripEventType.ChangeCountry ||
      previousCountryChange.tripEvent == TripEventType.Begin
    ) {
      if (
        (previousCountryChange.tripEvent == TripEventType.ChangeCountry &&
          (countryChangeMarkers[i].tripEvent == TripEventType.ChangeCountry ||
            countryChangeMarkers[i].tripEvent == TripEventType.Finish) &&
          previousCountryChange.country.id ==
            countryChangeMarkers[i].country.id) ||
        previousCountryChange.country.nationId == POLAND_COUNTRY_CODE
      ) {
        previousCountryChange = countryChangeMarkers[i]
        continue
      }
      potentialTransitPeriods.push({
        dateFrom: previousCountryChange.date,
        dateTo: countryChangeMarkers[i].date,
        country: previousCountryChange.country,
      })
    }
    previousCountryChange = countryChangeMarkers[i]
  }

  const transitPeriods = []
  for (const potentialTransitPeriod of potentialTransitPeriods) {
    if (
      !tripItems.some(
        (t) =>
          (t.tripEvent == TripEventType.Loading ||
            t.tripEvent == TripEventType.Unloading) &&
          t.date > potentialTransitPeriod.dateFrom &&
          t.date < potentialTransitPeriod.dateTo,
      )
    ) {
      const parentTmsData = tmsData.filter(
        (d) =>
          d.loadingDate < potentialTransitPeriod.DateFrom &&
          d.unloadingDate > potentialTransitPeriod.DateFrom,
      )
      if (parentTmsData.length > 0) {
        if (
          !parentTmsData.some(
            (p) => p.loadingCountry.name == potentialTransitPeriod.country.name,
          )
        ) {
          transitPeriods.push(potentialTransitPeriod)
        }
      } else {
        transitPeriods.push(potentialTransitPeriod)
      }
    }
  }

  for (const transitPeriod of transitPeriods) {
    const tmsItems = tripItems
      .filter(
        (t) =>
          (t.tripEvent == TripEventType.Loading ||
            t.tripEvent == TripEventType.Unloading) &&
          ((t.date >= transitPeriod.dateTo &&
            t.secondDate <= transitPeriod.dateFrom) ||
            (t.date <= transitPeriod.dateFrom &&
              t.secondDate >= transitPeriod.dateTo)),
      )
      .sort((a, b) => sortArray(a, b, 'date'))

    const associatedTms: Array<TmsDataModel> = []
    if (tmsItems.length > 0) {
      for (let i = 0; i < tmsItems.length; i++) {
        const currentTms = tmsItems[i]
        if (!associatedTms.find((t) => t.docNumber == currentTms.docNumber)) {
          associatedTms.push({
            docNumber: currentTms.docNumber,
            displayedLoadingDate: currentTms.date,
            displayedUnloadingDate: currentTms.secondDate,
            loadingDate: currentTms.date,
            unloadingDate: currentTms.secondDate,
            routeType: currentTms.tmsRouteType,
            transitCountry: transitPeriod.country,
            loadingCountry: currentTms.country,
            unloadingCountry: currentTms.secondCountry,
          })
        }
      }
    }
    tmsData.push({
      displayedLoadingDate: transitPeriod.dateFrom,
      displayedUnloadingDate: transitPeriod.dateTo,
      loadingDate: transitPeriod.dateFrom,
      unloadingDate: transitPeriod.dateTo,
      routeType: TmsRouteType.Transit,
      transitCountry: transitPeriod.country,
      associatedTms: associatedTms,
    })
  }
}

const determinePolishPeriods = (
  tmsData: Array<TmsDataModel>,
  tripItems: Array<EventModel>,
) => {
  const countryChangeMarkers = tripItems
    .filter(
      (t) =>
        t.tripEvent == TripEventType.ChangeCountry ||
        t.tripEvent == TripEventType.Begin ||
        t.tripEvent == TripEventType.Loading ||
        t.tripEvent == TripEventType.Unloading,
    )
    .sort(sortEvents)

  let previousCountryChange: EventModel = null
  if (countryChangeMarkers.length > 0) {
    previousCountryChange = countryChangeMarkers[0]
  }

  const potentialTransitPeriods = []
  let beginOccured = false

  for (let i = 1; i < countryChangeMarkers.length; i++) {
    beginOccured =
      (beginOccured ||
        previousCountryChange.tripEvent == TripEventType.Begin ||
        previousCountryChange.tripEvent == TripEventType.ChangeCountry) &&
      previousCountryChange.tripEvent != TripEventType.Finish
    if (!beginOccured) {
      previousCountryChange = countryChangeMarkers[i]
      continue
    }

    if (
      (previousCountryChange.tripEvent == TripEventType.ChangeCountry ||
        previousCountryChange.tripEvent == TripEventType.Begin) &&
      previousCountryChange.country.nationId == POLAND_COUNTRY_CODE
    ) {
      if (
        previousCountryChange.tripEvent == TripEventType.ChangeCountry &&
        countryChangeMarkers[i].tripEvent == TripEventType.ChangeCountry &&
        previousCountryChange.country.id == countryChangeMarkers[i].country.id
      ) {
        continue
      }
      potentialTransitPeriods.push({
        dateFrom: previousCountryChange.date,
        dateTo: countryChangeMarkers[i].date,
        country: previousCountryChange.country,
      })
    }
    previousCountryChange = countryChangeMarkers[i]
  }

  const transitPeriods = []
  for (const potentialTransitPeriod of potentialTransitPeriods) {
    if (
      !tripItems.some(
        (t) =>
          (t.tripEvent == TripEventType.Loading ||
            t.tripEvent == TripEventType.Unloading) &&
          t.date > potentialTransitPeriod.dateFrom &&
          t.date < potentialTransitPeriod.dateTo,
      )
    ) {
      if (
        !tmsData.some(
          (t) =>
            (t.routeType == TmsRouteType.Bilateral ||
              t.routeType == TmsRouteType.Import ||
              t.routeType == TmsRouteType.Export) &&
            t.unloadingDate > potentialTransitPeriod.dateFrom &&
            t.loadingDate < potentialTransitPeriod.dateTo,
        )
      ) {
        transitPeriods.push(potentialTransitPeriod)
      }
    }
  }

  for (const transitPeriod of transitPeriods) {
    const tmsItems = tripItems
      .filter(
        (t) =>
          (t.tripEvent == TripEventType.Loading ||
            t.tripEvent == TripEventType.Unloading) &&
          t.tmsRouteType != TmsRouteType.Undefined &&
          ((t.date >= transitPeriod.dateTo &&
            t.secondDate <= transitPeriod.dateFrom) ||
            (t.date <= transitPeriod.dateFrom &&
              t.secondDate >= transitPeriod.dateTo)),
      )
      .sort((a, b) => sortArray(a, b, 'date'))

    const associatedTms: Array<TmsDataModel> = []
    if (tmsItems.length > 0) {
      for (let i = 0; i < tmsItems.length; i++) {
        const currentTms = tmsItems[i]
        if (!associatedTms.find((t) => t.docNumber == currentTms.docNumber)) {
          associatedTms.push({
            docNumber: currentTms.docNumber,
            displayedLoadingDate: currentTms.date,
            displayedUnloadingDate: currentTms.secondDate,
            loadingDate: currentTms.date,
            unloadingDate: currentTms.secondDate,
            routeType: currentTms.tmsRouteType,
            transitCountry: transitPeriod.country,
            loadingCountry: currentTms.country,
            unloadingCountry: currentTms.secondCountry,
          })
        }
      }
    }
    tmsData.push({
      displayedLoadingDate: transitPeriod.dateFrom,
      displayedUnloadingDate: transitPeriod.dateTo,
      loadingDate: transitPeriod.dateFrom,
      unloadingDate: transitPeriod.dateTo,
      routeType: TmsRouteType.PolishTransit,
      transitCountry: transitPeriod.country,
      associatedTms: associatedTms,
    })
  }
}

const determineEmptyCabotagePeriods = (
  tmsData: Array<TmsDataModel>,
  tripItems: Array<EventModel>,
) => {
  const cabotageItems = tmsData.filter(
    (t) => t.routeType == TmsRouteType.Cabotage,
  )
  for (const cabotageItem of cabotageItems) {
    const shouldInsertEmptyCabotage = !tmsData.some(
      (o) =>
        o.unloadingDate >= cabotageItem.loadingDate &&
        o.loadingDate < cabotageItem.loadingDate,
    )
    if (shouldInsertEmptyCabotage) {
      const previousItems = tripItems
        .filter(
          (t) =>
            t.date < cabotageItem.loadingDate &&
            (t.tripEvent == TripEventType.Unloading ||
              t.tripEvent == TripEventType.ChangeCountry),
        )
        .sort(sortEvents)
      if (previousItems.length > 0) {
        let lastPreviousItem = null
        for (let i = previousItems.length - 1; i >= 0; i--) {
          if (previousItems[i].tripEvent == TripEventType.ChangeCountry) {
            if (previousItems[i].country.id == cabotageItem.loadingCountry.id) {
              lastPreviousItem = previousItems[i]
            } else {
              break
            }
          }

          if (previousItems[i].tripEvent == TripEventType.Unloading) {
            lastPreviousItem = previousItems[i]
            break
          }
        }

        if (lastPreviousItem != null) {
          tmsData.push({
            docNumber: cabotageItem.docNumber,
            displayedLoadingDate: lastPreviousItem.date,
            displayedUnloadingDate: cabotageItem.loadingDate,
            loadingDate: lastPreviousItem.date,
            unloadingDate: cabotageItem.loadingDate,
            routeType: TmsRouteType.CabotageEmpty,
            isEmptyLoad: true,
          })
        }
      }
    }
  }
}

const determineEmptyCrossTradePeriods = (
  tmsData: Array<TmsDataModel>,
  tripItems: Array<EventModel>,
) => {
  const crossTradeItems = tmsData.filter(
    (t) =>
      t.routeType == TmsRouteType.CrossTrade ||
      t.routeType == TmsRouteType.CrossTradeBilateral ||
      t.routeType == TmsRouteType.CrossTradeWrong,
  )
  for (const crossTradeItem of crossTradeItems) {
    const shouldInsertEmptyCrossTrade = !tmsData.some(
      (o) =>
        o.unloadingDate >= crossTradeItem.loadingDate &&
        o.loadingDate < crossTradeItem.loadingDate,
    )
    if (shouldInsertEmptyCrossTrade) {
      const previousItems = tripItems
        .filter(
          (t) =>
            t.date < crossTradeItem.loadingDate &&
            (t.tripEvent == TripEventType.Unloading ||
              t.tripEvent == TripEventType.ChangeCountry),
        )
        .sort(sortEvents)
      if (previousItems.length > 0) {
        let lastPreviousItem = null
        for (let i = previousItems.length - 1; i >= 0; i--) {
          lastPreviousItem = previousItems[i]
          if (lastPreviousItem.tripEvent == TripEventType.Unloading) {
            break
          }
        }

        if (lastPreviousItem != null) {
          tmsData.push({
            docNumber: crossTradeItem.docNumber,
            displayedLoadingDate: lastPreviousItem.date,
            displayedUnloadingDate: crossTradeItem.loadingDate,
            loadingDate: lastPreviousItem.date,
            unloadingDate: crossTradeItem.loadingDate,
            routeType:
              crossTradeItem.routeType == TmsRouteType.CrossTradeBilateral
                ? TmsRouteType.CrossTradeBilateralEmpty
                : TmsRouteType.CrossTradeWrongEmpty,
            isEmptyLoad: true,
          })
        }
      }
    }
  }
}

const determineEmptyPeriods = (
  tmsData: Array<TmsDataModel>,
  tripItems: Array<EventModel>,
) => {
  const bilateralItems = tmsData.filter(
    (t) =>
      t.routeType == TmsRouteType.Bilateral ||
      t.routeType == TmsRouteType.Import ||
      t.routeType == TmsRouteType.Export ||
      t.routeType == TmsRouteType.Domestic ||
      t.routeType == TmsRouteType.Undefined,
  )
  for (const bilateralItem of bilateralItems) {
    const shouldInsertEmptyPeriod = !tmsData.some(
      (o) =>
        o.unloadingDate >= bilateralItem.loadingDate &&
        o.loadingDate < bilateralItem.loadingDate,
    )
    if (shouldInsertEmptyPeriod) {
      const previousItems = tripItems
        .filter(
          (t) =>
            t.date < bilateralItem.loadingDate &&
            (t.tripEvent == TripEventType.Unloading ||
              t.tripEvent == TripEventType.ChangeCountry),
        )
        .sort(sortEvents)
      if (previousItems.length > 0) {
        let lastPreviousItem = null
        for (let i = previousItems.length - 1; i >= 0; i--) {
          lastPreviousItem = previousItems[i]
          if (
            lastPreviousItem.tripEvent == TripEventType.Unloading ||
            lastPreviousItem.tripEvent == TripEventType.Begin
          ) {
            break
          }
        }

        if (lastPreviousItem != null) {
          tmsData.push({
            displayedLoadingDate: lastPreviousItem.date,
            displayedUnloadingDate: bilateralItem.loadingDate,
            loadingDate: lastPreviousItem.date,
            unloadingDate: bilateralItem.loadingDate,
            routeType: TmsRouteType.Empty,
            isEmptyLoad: true,
          })
        }
      }
    }
  }

  const unloadings = tripItems.filter(
    (t) => t.tripEvent == TripEventType.Unloading,
  )
  const finishes = tripItems.filter((t) => t.tripEvent == TripEventType.Finish)

  if (unloadings.length > 0 && finishes.length > 0) {
    const lastUnloading = unloadings[unloadings.length - 1]
    const lastFinish = finishes[finishes.length - 1]

    if (
      moment(lastUnloading.date).format(TIME_FORMAT) !=
      moment(lastFinish.date).format(TIME_FORMAT)
    ) {
      tmsData.push({
        displayedLoadingDate: lastUnloading.date,
        displayedUnloadingDate: lastFinish.date,
        loadingDate: lastUnloading.date,
        unloadingDate: lastFinish.date,
        routeType: TmsRouteType.Empty,
        isEmptyLoad: true,
      })
    }
  }
}

const removeCabotageInsideTransitPeriods = (
  tmsData: Array<TmsDataModel>,
  tripItems: Array<EventModel>,
) => {
  const transitPeriods = tmsData.filter(
    (t) =>
      t.routeType == TmsRouteType.Transit ||
      t.routeType == TmsRouteType.PolishTransit,
  )
  const cabotagePeriods = tmsData.filter(
    (t) =>
      t.routeType == TmsRouteType.Cabotage ||
      t.routeType == TmsRouteType.CabotageEmpty,
  )

  const newCabotages = []

  for (const cabotagePeriod of cabotagePeriods) {
    const transitsToCut = transitPeriods.filter(
      (c) =>
        c.loadingDate >= cabotagePeriod.loadingDate &&
        c.unloadingDate <= cabotagePeriod.unloadingDate,
    )
    if (transitsToCut.length > 0) {
      for (let i = 0; i < transitsToCut.length; i++) {
        const transitToCut = transitsToCut[i]
        const startDate =
          i == 0
            ? cabotagePeriod.loadingDate
            : transitsToCut[i - 1].unloadingDate
        const endDate =
          i == transitsToCut.length - 1
            ? cabotagePeriod.unloadingDate
            : transitsToCut[i + 1].loadingDate

        const cabotageIndex = tmsData.findIndex(
          (t) =>
            (t.routeType == TmsRouteType.Cabotage ||
              t.routeType == TmsRouteType.CabotageEmpty) &&
            moment(t.loadingDate).format(TIME_FORMAT) ==
              moment(cabotagePeriod.loadingDate).format(TIME_FORMAT) &&
            moment(t.unloadingDate).format(TIME_FORMAT) ==
              moment(cabotagePeriod.unloadingDate).format(TIME_FORMAT),
        )
        if (cabotageIndex != -1) {
          tmsData.splice(cabotageIndex, 1)
        }

        newCabotages.push({
          docNumber: cabotagePeriod.docNumber,
          displayedLoadingDate: startDate,
          displayedUnloadingDate: transitToCut.loadingDate,
          loadingDate: cabotagePeriod.loadingDate,
          unloadingDate: cabotagePeriod.unloadingDate,
          loadingCountry: cabotagePeriod.loadingCountry,
          unloadingCountry: cabotagePeriod.unloadingCountry,
          routeType: cabotagePeriod.routeType,
          isEmptyLoad: cabotagePeriod.isEmptyLoad,
        })
        newCabotages.push({
          docNumber: cabotagePeriod.docNumber,
          displayedLoadingDate: transitToCut.unloadingDate,
          displayedUnloadingDate: endDate,
          loadingDate: cabotagePeriod.loadingDate,
          unloadingDate: cabotagePeriod.unloadingDate,
          loadingCountry: cabotagePeriod.loadingCountry,
          unloadingCountry: cabotagePeriod.unloadingCountry,
          routeType: cabotagePeriod.routeType,
          isEmptyLoad: cabotagePeriod.isEmptyLoad,
        })
      }
    } else {
      newCabotages.push(cabotagePeriod)
    }
  }

  const cabotagesToAdd = newCabotages.filter(
    (c) =>
      moment(c.loadingDate).format(TIME_FORMAT) !=
      moment(c.unloadingDate).format(TIME_FORMAT),
  )
  tmsData.push(...cabotagesToAdd)
}

const removeCrossTradeInsideTransitPeriods = (
  tmsData: Array<TmsDataModel>,
  tripItems: Array<EventModel>,
) => {
  const transitPeriods = tmsData.filter(
    (t) =>
      t.routeType == TmsRouteType.Transit ||
      t.routeType == TmsRouteType.PolishTransit,
  )
  const crossTradePeriods = tmsData.filter(
    (t) =>
      t.routeType == TmsRouteType.CrossTradeWrong ||
      t.routeType == TmsRouteType.CrossTradeWrongEmpty,
  )

  const newCrossTrades = []

  for (const crossTradePeriod of crossTradePeriods) {
    const transitsToCut = transitPeriods.filter(
      (c) =>
        c.loadingDate >= crossTradePeriod.loadingDate &&
        c.unloadingDate <= crossTradePeriod.unloadingDate,
    )
    if (transitsToCut.length > 0) {
      for (let i = 0; i < transitsToCut.length; i++) {
        const transitToCut = transitsToCut[i]
        const startDate =
          i == 0
            ? crossTradePeriod.loadingDate
            : transitsToCut[i - 1].unloadingDate
        const endDate =
          i == transitsToCut.length - 1
            ? crossTradePeriod.unloadingDate
            : transitsToCut[i + 1].loadingDate

        const crossTradeIndex = tmsData.findIndex(
          (t) =>
            (t.routeType == TmsRouteType.Cabotage ||
              t.routeType == TmsRouteType.CabotageEmpty) &&
            moment(t.loadingDate).format(TIME_FORMAT) ==
              moment(crossTradePeriod.loadingDate).format(TIME_FORMAT) &&
            moment(t.unloadingDate).format(TIME_FORMAT) ==
              moment(crossTradePeriod.unloadingDate).format(TIME_FORMAT),
        )
        if (crossTradeIndex != -1) {
          tmsData.splice(crossTradeIndex, 1)
        }

        newCrossTrades.push({
          docNumber: crossTradePeriod.docNumber,
          displayedLoadingDate: startDate,
          displayedUnloadingDate: transitToCut.loadingDate,
          loadingDate: crossTradePeriod.loadingDate,
          unloadingDate: crossTradePeriod.unloadingDate,
          loadingCountry: crossTradePeriod.loadingCountry,
          unloadingCountry: crossTradePeriod.unloadingCountry,
          routeType: crossTradePeriod.routeType,
          isEmptyLoad: crossTradePeriod.isEmptyLoad,
        })
        newCrossTrades.push({
          docNumber: crossTradePeriod.docNumber,
          displayedLoadingDate: transitToCut.unloadingDate,
          displayedUnloadingDate: endDate,
          loadingDate: crossTradePeriod.loadingDate,
          unloadingDate: crossTradePeriod.unloadingDate,
          loadingCountry: crossTradePeriod.loadingCountry,
          unloadingCountry: crossTradePeriod.unloadingCountry,
          routeType: crossTradePeriod.routeType,
          isEmptyLoad: crossTradePeriod.isEmptyLoad,
        })
      }
    } else {
      newCrossTrades.push(crossTradePeriod)
    }
  }

  const crossTradesToAdd = newCrossTrades.filter(
    (c) =>
      moment(c.loadingDate).format(TIME_FORMAT) !=
      moment(c.unloadingDate).format(TIME_FORMAT),
  )
  tmsData.push(...crossTradesToAdd)
}
